import { Box, Button, Typography, FormHelperText, Chip, ChipProps } from '@mui/material'
import { yellow, blue } from '@mui/material/colors'
import { EditSubGroupUsersPayload, NewSubGroupPayload } from 'api/management'
import Field from 'components/ui/Field'
import FormInput from 'components/ui/FormInput'
import MultiSelect from 'components/ui/MultiSelect'
import { ERROR_TEXT, OPERATION_FLOW_TYPES } from 'config'
import { useConfirm } from 'hooks/useDialog'
import useLeavingPrompt from 'hooks/useLeavingPrompt'
import { useFormContext, Controller } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { User } from 'types'
import applyErrorMsg from 'utils/applyErrorMsg'
import { isAdminOrManager, isGroupUserAmountEnough } from 'utils/businessRules'
import { GROUP_NAME } from 'utils/regex'

import { useLoaderData } from './GroupConfig.loader'
import OperationFlowTypeGraph from './OperationFlowTypeGraph'

type ConfirmReturn = Promise<{
  result?: any
  status: 'confirmed' | 'canceled'
}>

interface Props {
  title: string
  mode: 'edit' | 'new'
  onSave?: (data: EditSubGroupUsersPayload) => void
  onAdd?: (data: NewSubGroupPayload) => void
  onDel?: (data: any) => void
}

const operationFlowTypeOptions = Object.entries(OPERATION_FLOW_TYPES).map(([value, label]) => ({
  value,
  label,
}))

const USER_NOT_ENOUGH_MSGS = {
  doubleCheckVerify:
    '「ダブルチェック＋ベリファイ」フローの運用では、2名以上の「登録担当者」もしくは「一般ユーザー」と、1名以上の運用管理者が必要となります',
  tripleCheckVerify:
    '「トリプルチェック＋ベリファイ」フローの運用では、3名以上の「登録担当者」もしくは「一般ユーザー」と、1名以上の運用管理者が必要となります',
} as const

export default function GroupFormFields({ title, mode, onSave, onAdd, onDel }: Props) {
  const isEditMode = mode === 'edit'
  const confirm = useConfirm()
  const {
    watch,
    handleSubmit,
    formState: { isSubmitted, isSubmitting, isDirty },
  } = useFormContext()
  const selectedOperationFlowType = watch('operationFlowType')

  const confirmUserAmount = (data): ConfirmReturn => {
    const { users, operationFlowType } = data
    const isUserEnough = isGroupUserAmountEnough({ users, operationFlowType })

    if (isUserEnough) {
      return Promise.resolve({ status: 'confirmed' })
    }

    return confirm({
      message: USER_NOT_ENOUGH_MSGS[operationFlowType],
      cancelText: 'キャンセル',
      actionText: '確認',
    })
  }

  useLeavingPrompt({
    message: ['登録をキャンセルします', '入力したデータは保存されません。'],
    when: isDirty && !(isSubmitted || isSubmitting),
  })

  const onSubmit = handleSubmit(async (data) => {
    const { status } = await confirmUserAmount(data)

    if (status !== 'confirmed') {
      return
    }
    const users = data.users.map((u) => u.email)
    if (isEditMode) {
      onSave &&
        onSave({
          name: data.name,
          users,
        })
    } else {
      onAdd &&
        onAdd({
          name: data.name,
          operationFlowType: data.operationFlowType,
          users,
        })
    }
  })

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography component="h3" variant="h6">
          {title}
        </Typography>
        <div>
          <Button
            variant="outlined"
            component={Link}
            color="secondary"
            to=".."
            data-testid="cancel-button"
          >
            キャンセル
          </Button>
          {mode === 'edit' && (
            <Button
              variant="contained"
              color="secondary"
              onClick={onDel}
              data-testid="delete-button"
            >
              削除
            </Button>
          )}
          <Button variant="contained" color="primary" onClick={onSubmit} data-testid="save-button">
            保存
          </Button>
        </div>
      </Box>

      <Box
        sx={{
          width: '50%',
          minWidth: 400,
        }}
      >
        <Field label="グループ名">
          <FormInput
            disabled={isEditMode}
            name="name"
            type="text"
            rules={applyErrorMsg({
              required: true,
              pattern: { value: GROUP_NAME, message: '無効な入力です' },
            })}
            testid="group-name"
          />
        </Field>

        <Field label="所属するユーザー">
          <UserSelect />
        </Field>

        <Field label="運用フロータイプの選択">
          <FormInput
            disabled={isEditMode}
            name="operationFlowType"
            type="select"
            options={operationFlowTypeOptions}
            testid="operation-flow-type"
          />
        </Field>
      </Box>

      <OperationFlowTypeGraph type={selectedOperationFlowType} />
    </div>
  )
}

const AdminChip = (props: ChipProps) => <Chip {...props} sx={{ backgroundColor: yellow[300] }} />
const UserChip = (props: ChipProps) => <Chip {...props} sx={{ backgroundColor: blue[200] }} />

function UserSelect() {
  const {
    control,
    formState: { errors },
  } = useFormContext()
  const { users } = useLoaderData()

  return (
    <div>
      <Controller
        control={control}
        name="users"
        rules={{ validate: (val) => !!val.length }}
        render={({ field: { onChange, value } }) => {
          const newValue = value.map((v) => users.find((u) => u.userID === v.userID))
          return (
            <MultiSelect
              disableClearable
              options={users}
              getOptionLabel={(opt: User) => opt.name}
              onChange={(e, val) => {
                onChange(val)
              }}
              value={newValue}
              inputProps={{
                error: !!errors.users,
              }}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((user: User, index) => {
                  const Comp = isAdminOrManager(user) ? AdminChip : UserChip
                  return <Comp label={user.name} {...getTagProps({ index })} key={user.userID} />
                })
              }
              data-testid="users"
            />
          )
        }}
      />
      {errors?.users && <FormHelperText error>{ERROR_TEXT.required}</FormHelperText>}
    </div>
  )
}
